import { StyleSheet, css } from 'aphrodite'
import type React from 'react'
import { useEffect, useMemo, useState } from 'react'
import { userService } from '../../services/userService'
import type { User } from '../../types/user'
import MultiSelect from '../MultiSelect'
import type { Option } from '../MultiSelect'

interface DriverOption {
  id: number
  name: string
  email: string
  isTeamMember: boolean
  userId: number
}

interface DriverSelectionProps {
  selectedDriverIds: number[]
  onDriversChange: (driverIds: number[]) => void
  isDisabled?: boolean
}

const DriverSelection: React.FC<DriverSelectionProps> = ({
  selectedDriverIds,
  onDriversChange,
  isDisabled = false,
}) => {
  const [drivers, setDrivers] = useState<DriverOption[]>([])
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const fetchDrivers = async () => {
      setIsLoading(true)
      try {
        const [contacts, teamMembers] = await Promise.all([
          userService.getContacts(),
          userService.getTeamMembers(),
        ])

        // Get driver contacts and convert to DriverOption
        const driverContacts: DriverOption[] = contacts
          .filter(
            (c) =>
              c.contact_object?.user_type === 'DRIVER' &&
              c.contact_object.user_id,
          )
          .map((contact) => ({
            id: contact.contact_object.user_id as number,
            name: contact.contact_object.name || contact.contact_object.email,
            email: contact.contact_object.email,
            isTeamMember: false,
            userId: contact.contact_object.user_id as number,
          }))

        // Get team members with DRIVER user type and convert to DriverOption
        const driverTeamMembers: DriverOption[] = teamMembers
          .filter((user: User) => user.user_type === 'DRIVER')
          .map((user: User) => ({
            id: user.id,
            name: `${user.first_name} ${user.last_name}`.trim() || user.email,
            email: user.email,
            isTeamMember: true,
            userId: user.id,
          }))

        // Combine and deduplicate by user ID (team members take precedence)
        const allDrivers = [...driverContacts, ...driverTeamMembers]
        const uniqueDrivers = allDrivers.filter(
          (driver, index, self) =>
            index === self.findIndex((d) => d.userId === driver.userId),
        )

        setDrivers(uniqueDrivers)
      } catch (error) {
        console.error('Failed to fetch drivers:', error)
      } finally {
        setIsLoading(false)
      }
    }

    fetchDrivers()
  }, [])

  // Convert selected driver IDs to Options for MultiSelect
  const selectedOptions = useMemo(() => {
    return drivers
      .filter((driver) => selectedDriverIds.includes(driver.userId))
      .map((driver) => ({
        label: `${driver.name} (${driver.email})${driver.isTeamMember ? ' - Team Member' : ''}`,
        value: driver.userId.toString(),
        name: driver.name,
        email: driver.email,
      }))
  }, [drivers, selectedDriverIds])

  // Convert drivers to options for MultiSelect
  const driverOptions = useMemo(() => {
    return drivers.map((driver) => ({
      label: `${driver.name} (${driver.email})${driver.isTeamMember ? ' - Team Member' : ''}`,
      value: driver.userId.toString(),
      name: driver.name,
      email: driver.email,
    }))
  }, [drivers])

  const handleDriverSelection = (selected: Option[] | Option | null) => {
    if (Array.isArray(selected)) {
      const driverIds = selected.map((option) =>
        Number.parseInt(option.value, 10),
      )
      onDriversChange(driverIds)
    } else {
      // Single selection mode (shouldn't happen but handle gracefully)
      onDriversChange(selected ? [Number.parseInt(selected.value, 10)] : [])
    }
  }

  return (
    <div className={css(styles.container)}>
      <div className={css(styles.formGroup)}>
        <label className={css(styles.label)}>
          Assign Drivers{' '}
          <span className={css(styles.optional)}>(optional)</span>
        </label>
        <p className={css(styles.description)}>
          Selected drivers will be automatically added to the dispatcher's
          contact list.
        </p>
        <MultiSelect
          isMulti={true}
          placeholder={
            isLoading ? 'Loading drivers...' : 'Select drivers to assign'
          }
          options={driverOptions}
          value={selectedOptions}
          showEmailInOption={true}
          isDisabled={isDisabled || isLoading}
          onChange={handleDriverSelection}
        />
        {selectedDriverIds.length > 0 && (
          <p className={css(styles.selectedCount)}>
            {selectedDriverIds.length} driver
            {selectedDriverIds.length === 1 ? '' : 's'} selected
          </p>
        )}
      </div>
    </div>
  )
}

const styles = StyleSheet.create({
  container: {
    marginBottom: '24px',
  },
  formGroup: {
    marginBottom: '16px',
  },
  label: {
    display: 'block',
    fontSize: '14px',
    fontWeight: 600,
    color: '#374151',
    marginBottom: '8px',
  },
  optional: {
    fontWeight: 400,
    color: '#6B7280',
  },
  description: {
    fontSize: '12px',
    color: '#6B7280',
    marginBottom: '8px',
    margin: 0,
  },
  selectedCount: {
    fontSize: '12px',
    color: '#059669',
    marginTop: '4px',
    margin: 0,
  },
})

export default DriverSelection
